<template>
  <CardBase :headerBorder="false">
    <template #header>{{ $route.meta.title }}</template>
    <template #toolbar>
      <router-link
        v-if="userCan('create.user')"
        :to="{ name: 'UserCreate' }"
        class="button button--primary poppins"
      >
        <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-1" />New
        User
      </router-link>
    </template>
    <template #content>
      <form class="form form-search" method="get" @submit.prevent="reloadTable">
        <div class="form-search__row">
          <div class="form-search__double">
            <label class="form__label">Search</label>
            <input
              type="text"
              class="form__input"
              placeholder="Name / Username"
              v-model="formData.search"
            />
          </div>
          <div class="form-search__double">
            <label class="form__label">Asset Group</label>
            <select
              class="form__input"
              v-model="formData.group_id"
              @change="setGroup"
            >
              <option value="" v-if="listGroup.length > 1">Show All</option>
              <option value="" v-if="listGroupLoading">Please wait</option>
              <option v-for="row in listGroup" :key="row.id" :value="row.id">
                {{ row.code_name }}
              </option>
            </select>
          </div>
        </div>

        <div class="form-search__row">
          <div class="form-search__double">
            <label class="form__label">Role</label>
            <select
              class="form__input"
              v-model="formData.role_id"
              :disabled="!formData.group_id"
            >
              <option value="" v-if="!formData.group_id">
                Choose group first
              </option>
              <option value="" v-else-if="listUserRoleLoading">
                Please wait
              </option>
              <option value="" v-else>Show All</option>

              <option v-for="row in listUserRole" :key="row.id" :value="row.id">
                {{ row.name }}
              </option>
            </select>
          </div>
          <div class="form-search__double">
            <label class="form__label">Status</label>
            <select class="form__input" v-model="formData.status">
              <option value="">Show All</option>
              <option value="0">Inactive</option>
              <option value="1">Active</option>
              <option value="2">Banned</option>
            </select>
          </div>
        </div>

        <div class="form-search__row" v-if="userCan('export.crm')">
          <div class="form-search__double">
            <label class="form__label">Bypass</label>
            <select class="form__input" v-model="formData.bypass">
              <option value="">Show All</option>
              <option value="0">No</option>
              <option value="1">Yes</option>
            </select>
          </div>
        </div>

        <div class="form-search__action">
          <div class="w-full flex">
            <BaseButton type="submit" buttonClass="secondary"
              >Search</BaseButton
            >
            <BaseButton
              class="mx-2"
              type="button"
              buttonClass="default"
              @click="reset"
              >Reset Search</BaseButton
            >
          </div>
        </div>
      </form>

      <vue-good-table
        :key="tableKey"
        mode="remote"
        theme="polar-bear"
        :rows="tableData.rows"
        :columns="tableData.columns"
        :total-rows="tableData.totalRows"
        :isLoading="tableData.isLoading"
        :sort-options="{
          enabled: false,
        }"
        :pagination-options="{
          enabled: true,
          perPageDropdown: perPageDropdown,
          dropdownAllowAll: false,
          setCurrentPage: tableData.currentPage,
          infoFn: tablePageInfo,
        }"
        styleClass="vgt-table striped bordered"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field == 'uuid'">
            <router-link
              v-if="userCan('update.user')"
              :to="{ name: 'UserEdit', params: { id: props.row.uuid } }"
              v-tooltip="'Edit ' + props.row.name"
              class="table-action__link mx-2"
            >
              <font-awesome-icon :icon="['fas', 'edit']" />
            </router-link>
            <button
              v-if="userCan('delete.user')"
              class="table-action__link"
              v-tooltip="'Delete ' + props.row.name"
              @click="deleteItem(props.row.uuid, props.row.name)"
            >
              <font-awesome-icon :icon="['far', 'trash-alt']" />
            </button>
          </span>
          <span v-else-if="props.column.field == 'bypass' && props.row.bypass">
            <span class="block"
              >{{ dateFormat(props.row.bypass.last_used_at) }}<br />
              {{ props.row.bypass.last_used_code }}</span
            >
            <hr class="my-2">
            <strong>History:</strong>
            <ul>
              <li v-for="hist in props.row.bypass.history" :key="hist.code">
                {{ hist.count }}x {{ hist.code }}
              </li>
            </ul>
          </span>
          <span v-else-if="props.column.field == 'status'">
            <span class="flex items-center" v-if="props.row.status == 0">
              <font-awesome-icon
                :icon="['fas', 'times-circle']"
                class="text-yellow-500 mr-2"
                size="lg"
              />Inactive
            </span>

            <span class="flex items-center" v-if="props.row.status == 1">
              <font-awesome-icon
                :icon="['fas', 'check-circle']"
                class="text-green-500 mr-2"
                size="lg"
              />Active
            </span>

            <span class="flex items-center" v-else-if="props.row.status == 2">
              <font-awesome-icon
                :icon="['fas', 'exclamation-triangle']"
                class="text-red-500 mr-2"
                size="lg"
              />Banned
            </span>
          </span>
        </template>
      </vue-good-table>
    </template>
  </CardBase>
</template>

<script>
import { showListMixin } from "@/mixins/showListMixin";

export default {
  mixins: [showListMixin],
  data: () => {
    return {
      tableData: {
        columns: [
          {
            label: "Group",
            field: "group",
          },
          {
            label: "Name",
            field: "name",
          },
          {
            label: "Username",
            field: "username",
          },
          {
            label: "Role",
            field: "role",
          },
          {
            label: "Bypass",
            field: "bypass",
            hidden: true,
          },
          {
            label: "Status",
            field: "status",
          },
          {
            label: "",
            field: "uuid",
            thClass: "vgt-table__action",
            tdClass: "vgt-table__action",
          },
        ],
        sort_by: "username",
        sort_dir: "asc",
      },
      formData: {
        search: "",
        role_id: "",
        group_id: "",
        status: "",
        bypass: "",
      },
      listUserRoleLoading: true,
      listUserRole: [],

      listGroupLoading: true,
      listGroup: [],
    };
  },
  created() {
    this.getListGroup();
  },
  methods: {
    dateFormat(date) {
      return this.moment(date).format("DD MMM YYYY - HH:mm");
    },
    reset() {
      this.formData.search = "";
      this.formData.role_id = "";

      if (this.listGroup.length > 1) {
        this.formData.group_id = "";
      }
      this.formData.status = "";
      if (this.formData.bypass) {
        this.formData.bypass = "";
      }

      this.tableData.limit = 10;

      if (this.$route.query.search == undefined) {
        this.tableKey++;
      } else {
        this.tableData.currentPage = 1;
        this.$router.push({ name: "UserList" }).catch(() => {});
      }
    },
    reloadTable() {
      if (this.formData.search && this.formData.search.length < 3) {
        this.$swal({
          position: "bottom-end",
          showConfirmButton: false,
          toast: true,
          timer: 3000,
          timerProgressBar: true,
          title: "Search query must have at least 3 characters",
          icon: "warning",
        });
      } else {
        this.tableData.currentPage = 1;
        let query = {
          search: this.formData.search,
          group_id: this.formData.group_id,
          status: this.formData.status,
          tz: new Date().getTime(),
        };

        if (this.formData.bypass) {
          query.bypass = this.formData.bypass;
        }

        if (this.formData.group_id) {
          query.role_id = this.formData.role_id;
        }

        this.$router
          .replace({
            query: query,
          })
          .catch(() => {});
      }
    },
    loadTableData() {
      const queryParams = this.$route.query;
      let searchParam = "";

      if (queryParams.search != undefined) {
        this.formData = { ...this.formData, ...this.$route.query };
        for (const key in queryParams) {
          searchParam += `&${key}=${queryParams[key]}`;
        }
      }

      if (this.formData.bypass == 1) {
        this.tableData.columns[4].hidden = false;
      } else {
        this.tableData.columns[4].hidden = true;
      }

      this.tableData.isLoading = true;
      this.$http
        .get(
          process.env.VUE_APP_API_URL +
            `${this.$route.path}?page=${this.tableData.currentPage}&limit=${this.tableData.limit}&sort_by=${this.tableData.sort_by}&sort_dir=${this.tableData.sort_dir}${searchParam}`
        )
        .then((res) => {
          this.tableData.rows = res.data.data;
          this.tableData.totalRows = res.data.total;
        })
        .finally(() => {
          this.tableData.isLoading = false;
        });
    },
    getListGroup() {
      this.listGroupLoading = true;
      this.$http
        .get(process.env.VUE_APP_API_URL + "/group")
        .then((res) => {
          if (res.status == 200) {
            const response = res.data;

            if (response) {
              this.listGroup = response.data;
              if (this.$route.query.group_id != "" && this.formData.group_id != "") {
                // Ada search
                this.getListUserRole(this.$route.query.group_id, true);
              } else {
                if (this.listGroup.length == 1) {
                  this.getListUserRole(this.listGroup[0].id);
                  this.formData.group_id = this.listGroup[0].id;
                }
              }
            }
          }
        })
        .finally(() => {
          this.listGroupLoading = false;
        });
    },
    setGroup() {
      this.getListUserRole(this.formData.group_id);
    },
    getListUserRole(groupId = "", preSelect = false) {
      this.listUserRoleLoading = true;

      this.$http
        .get(process.env.VUE_APP_API_URL + "/role?group_id=" + groupId)
        .then((res) => {
          if (res.status == 200) {
            const response = res.data;

            if (response) {
              this.listUserRole = response.data;

              if (preSelect) {
                this.formData.role_id = this.$route.query.role_id;
              } else {
                this.formData.role_id = "";
              }
            }
          }
        })
        .finally(() => {
          this.listUserRoleLoading = false;
        });
    },
  },
};
</script>
